import * as Sentry from '@sentry/vue';
import {log} from '@/utils/logger';
import {useContextStore} from '@/store/context.store';

const REQUEST_TIMEOUT_MS = 20000;

export type HttpParams = Record<string, string | number | undefined>;

export type HttpResponse<T> = {
	data: T;
	status: number;
};

export class HttpError extends Error {
	constructor(readonly url: string, readonly status: number | undefined, message: string) {
		super(message);
	}
}

function withParams(url: string, params?: HttpParams): string {
	if (!params) {
		return url;
	}
	const search = new URLSearchParams();
	Object.entries(params)
		.filter(([, value]) => value !== undefined && value !== null)
		.forEach(([name, value]) => search.append(name, String(value)));
	const query = search.toString();
	return query ? `${url}?${query}` : url;
}

/** Reports a failed request the way the former vue-resource interceptor did: log, Sentry, warning in the store. */
function reportFailure(url: string, reason: string | number): void {
	const errorMsg = `Error getting data '${url}': ${reason}`;
	log.error(errorMsg);
	if (import.meta.env.PROD) {
		Sentry.captureMessage(errorMsg, 'error');
	}
	useContextStore().addWarning(`Fehler beim Laden von ${url}: ${reason}`);
}

export async function get<T>(url: string, options: {params?: HttpParams, timeout?: number} = {}): Promise<HttpResponse<T>> {
	const requestUrl = withParams(url, options.params);
	log.debug(`request to ${requestUrl}`);
	let response: Response;
	try {
		response = await fetch(requestUrl, {signal: AbortSignal.timeout(options.timeout ?? REQUEST_TIMEOUT_MS)});
	} catch (error) {
		const reason = error instanceof DOMException && error.name === 'TimeoutError' ? 'Timeout' : String(error);
		reportFailure(requestUrl, reason);
		throw new HttpError(requestUrl, undefined, reason);
	}
	if (!response.ok) {
		reportFailure(requestUrl, response.status);
		throw new HttpError(requestUrl, response.status, `Error getting data: ${response.status}`);
	}
	return {data: await parseBody<T>(response), status: response.status};
}

// the ping endpoint answers with plain text, everything else with JSON
async function parseBody<T>(response: Response): Promise<T> {
	const contentType = response.headers.get('content-type') ?? '';
	if (contentType.includes('application/json')) {
		return await response.json() as T;
	}
	// the caller's T decides how the text is read
	return await response.text() as T;
}
